/**
 * Bittersweet Herb Farm - Animations & Easter Eggs
 * Tasteful scroll animations and delightful interactions
 */

(function() {
  'use strict';

  // ===================================
  // SCROLL-TRIGGERED ANIMATIONS
  // ===================================
  
  function initScrollAnimations() {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      console.log('[BHF Animations] Reduced motion detected, animations disabled');
      return;
    }

    // Create Intersection Observer for fade-in animations
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -100px 0px', // Trigger 100px before element enters viewport
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          // Optional: unobserve after animation to improve performance
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Select elements to animate
    const animateElements = document.querySelectorAll(`
      .product-card,
      .collection-card,
      .feature-card,
      .testimonial-card,
      .blog-card,
      .recipe-card,
      .benefit-item,
      .stat-item,
      .faq-item
    `);

    // Observe each element
    animateElements.forEach((el, index) => {
      // Add initial hidden state
      el.classList.add('animate-on-scroll');
      // Stagger animation delays
      el.style.transitionDelay = `${index * 50}ms`;
      observer.observe(el);
    });

    console.log(`[BHF Animations] Observing ${animateElements.length} elements for scroll animations`);
  }

  // ===================================
  // CARD HOVER ENHANCEMENTS
  // ===================================
  
  function enhanceCardHovers() {
    const cards = document.querySelectorAll('.product-card, .collection-card, .blog-card, .recipe-card');
    
    cards.forEach(card => {
      card.addEventListener('mouseenter', function(e) {
        this.style.transform = 'translateY(-8px) scale(1.02)';
      });
      
      card.addEventListener('mouseleave', function(e) {
        this.style.transform = 'translateY(0) scale(1)';
      });
    });
  }

  // ===================================
  // EASTER EGG: HERB SHOWER 🌿
  // ===================================
  
  let logoClickCount = 0;
  let logoClickTimer = null;
  const REQUIRED_CLICKS = 7;
  const CLICK_TIMEOUT = 3000; // Reset after 3 seconds of inactivity

  function initHerbShowerEasterEgg() {
    const logo = document.querySelector('.header-logo');
    if (!logo) return;

    logo.addEventListener('click', function(e) {
      // Don't trigger if clicking on mobile menu or other interactive elements
      if (e.target.closest('button, a[href]') && !e.target.closest('.header-logo')) {
        return;
      }

      logoClickCount++;
      
      // Clear existing timer
      if (logoClickTimer) {
        clearTimeout(logoClickTimer);
      }

      // Visual feedback for each click (subtle pulse)
      if (logoClickCount < REQUIRED_CLICKS) {
        logo.style.animation = 'logoPulse 0.3s ease';
        setTimeout(() => {
          logo.style.animation = '';
        }, 300);
      }

      // Reset counter after timeout
      logoClickTimer = setTimeout(() => {
        logoClickCount = 0;
      }, CLICK_TIMEOUT);

      // Trigger herb shower on 7th click
      if (logoClickCount === REQUIRED_CLICKS) {
        triggerHerbShower();
        logoClickCount = 0; // Reset
        clearTimeout(logoClickTimer);
      }
    });

    console.log('[BHF Easter Egg] Herb shower initialized - click logo 7 times! 🌿');
  }

  function triggerHerbShower() {
    console.log('[BHF Easter Egg] 🌿 HERB SHOWER ACTIVATED! 🌿');
    
    // Create container
    const container = document.createElement('div');
    container.className = 'herb-shower-container';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 999999;
      overflow: hidden;
    `;
    document.body.appendChild(container);

    // Herb emoji options
    const herbs = ['🌿', '🍃', '🌱', '🌾', '🪴', '☘️', '🍀'];
    const herbCount = 50;

    // Create falling herbs
    for (let i = 0; i < herbCount; i++) {
      setTimeout(() => {
        createFallingHerb(container, herbs[Math.floor(Math.random() * herbs.length)]);
      }, i * 50); // Stagger creation
    }

    // Show success message
    showEasterEggMessage();

    // Clean up after animation
    setTimeout(() => {
      container.remove();
    }, 8000);
  }

  function createFallingHerb(container, herbEmoji) {
    const herb = document.createElement('div');
    herb.textContent = herbEmoji;
    herb.style.cssText = `
      position: absolute;
      top: -50px;
      left: ${Math.random() * 100}%;
      font-size: ${20 + Math.random() * 30}px;
      opacity: 0;
      transform: rotate(${Math.random() * 360}deg);
      animation: herbFall ${3 + Math.random() * 2}s ease-in forwards;
      animation-delay: ${Math.random() * 0.5}s;
    `;
    container.appendChild(herb);

    // Remove after animation
    setTimeout(() => {
      herb.remove();
    }, 6000);
  }

  function showEasterEggMessage() {
    const message = document.createElement('div');
    message.className = 'easter-egg-message';
    message.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: linear-gradient(135deg, var(--sage-800), var(--sage-700));
        color: white;
        padding: 2rem 3rem;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        z-index: 1000000;
        text-align: center;
        animation: easterEggPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        pointer-events: none;
      ">
        <div style="font-size: 3rem; margin-bottom: 0.5rem;">🌿✨</div>
        <div style="font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;">
          You Found It!
        </div>
        <div style="font-size: 1rem; opacity: 0.9;">
          May your herbs always be fresh! 🌱
        </div>
      </div>
    `;
    document.body.appendChild(message);

    // Remove after 3 seconds
    setTimeout(() => {
      message.style.opacity = '0';
      message.style.transition = 'opacity 0.5s ease';
      setTimeout(() => message.remove(), 500);
    }, 2500);
  }

  // ===================================
  // BUTTON RIPPLE EFFECT
  // ===================================
  
  function addButtonRipples() {
    const buttons = document.querySelectorAll('button, .btn, .button, [role="button"]');
    
    buttons.forEach(button => {
      if (button.dataset.bhfRippleBound === 'true') return;
      button.dataset.bhfRippleBound = 'true';

      button.addEventListener('click', function(e) {
        if (this.matches('[data-disable-ripple="true"], .product-add-to-cart, .sticky-buy-btn')) {
          return;
        }

        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        ripple.className = 'bhf-ripple-effect';
        ripple.style.width = `${size}px`;
        ripple.style.height = `${size}px`;
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;

        // Reset any stale ripple artifacts before adding a new one.
        this.querySelectorAll('.bhf-ripple-effect').forEach((node) => node.remove());
        this.classList.add('bhf-ripple-host');
        this.appendChild(ripple);

        const cleanup = () => ripple.remove();
        ripple.addEventListener('animationend', cleanup, { once: true });
        setTimeout(cleanup, 700);
      });
    });
  }

  // ===================================
  // PARALLAX SCROLL EFFECT (subtle)
  // ===================================
  
  function initParallaxEffect() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (parallaxElements.length === 0) return;
    
    window.addEventListener('scroll', () => {
      const scrolled = window.pageYOffset;
      
      parallaxElements.forEach(el => {
        const speed = el.dataset.parallax || 0.5;
        const yPos = -(scrolled * speed);
        el.style.transform = `translateY(${yPos}px)`;
      });
    }, { passive: true });
  }

  // ===================================
  // ADD ANIMATION STYLES
  // ===================================
  
  function injectAnimationStyles() {
    if (document.getElementById('bhf-animations-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'bhf-animations-styles';
    style.textContent = `
      /* Scroll-triggered animations */
      .animate-on-scroll {
        opacity: 0;
        transform: translateY(30px);
        transition: opacity 0.6s ease, transform 0.6s ease;
      }
      
      .animate-on-scroll.animate-in {
        opacity: 1;
        transform: translateY(0);
      }
      
      /* Logo pulse for easter egg */
      @keyframes logoPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      
      /* Herb falling animation */
      @keyframes herbFall {
        0% {
          opacity: 0;
          transform: translateY(0) rotate(0deg);
        }
        10% {
          opacity: 1;
        }
        90% {
          opacity: 1;
        }
        100% {
          opacity: 0;
          transform: translateY(100vh) rotate(720deg);
        }
      }
      
      /* Easter egg message pop */
      @keyframes easterEggPop {
        0% {
          transform: translate(-50%, -50%) scale(0);
          opacity: 0;
        }
        50% {
          transform: translate(-50%, -50%) scale(1.1);
        }
        100% {
          transform: translate(-50%, -50%) scale(1);
          opacity: 1;
        }
      }
      
      /* Button ripple */
      @keyframes ripple {
        0% {
          transform: scale(0);
          opacity: 1;
        }
        100% {
          transform: scale(4);
          opacity: 0;
        }
      }

      .bhf-ripple-host {
        position: relative;
        overflow: hidden;
      }

      .bhf-ripple-effect {
        position: absolute;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        pointer-events: none;
        animation: ripple 0.6s ease-out;
      }
      
      /* Enhanced card transitions */
      .product-card,
      .collection-card,
      .blog-card {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
                    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      /* Smooth page load fade-in */
      body {
        animation: pageLoad 0.3s ease-in;
      }
      
      @keyframes pageLoad {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      /* Respect reduced motion */
      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
        
        .animate-on-scroll {
          opacity: 1 !important;
          transform: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ===================================
  // INITIALIZATION
  // ===================================
  
  function init() {
    console.log('[BHF Animations] Initializing...');
    
    // Inject styles first
    injectAnimationStyles();
    
    // Initialize features
    initScrollAnimations();
    enhanceCardHovers();
    initHerbShowerEasterEgg();
    addButtonRipples();
    initParallaxEffect();
    
    console.log('[BHF Animations] ✨ All animations initialized!');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.BittersweetHerbFarm = window.BittersweetHerbFarm || {};
  window.BittersweetHerbFarm.animations = {
    triggerHerbShower,
    version: '1.0.0'
  };
})();

